import * as React from "react";

import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";

export type DeletePolicyVersionsDialogRequest = {
  policyVersionIds: string[];
};

export type DeletePolicyVersionsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DeletePolicyVersionsDialogRequest | null;
  activeComposedPolicyVersionIds: string[];
  isDeleting?: boolean;
  onConfirm: () => void;
};

function deletePolicyVersionsMessage(
  policyVersionIds: string[],
  activeComposedPolicyVersionIds: string[]
): string {
  const uniqueIds = Array.from(new Set(policyVersionIds.filter(Boolean)));
  const count = uniqueIds.length;
  if (count === 0) return "";

  const includesActiveComposed = uniqueIds.some((id) =>
    activeComposedPolicyVersionIds.includes(id)
  );
  const countLabel = `${count} policy version${count === 1 ? "" : "s"}`;

  if (count === 1) {
    return includesActiveComposed
      ? "Delete the policy version used in your active TradingView Pinescript? Re-compose after deleting to refresh active output. This cannot be undone."
      : "Delete this policy version permanently? This cannot be undone.";
  }

  if (includesActiveComposed) {
    return `Delete ${countLabel} permanently? This includes a version used in your active TradingView Pinescript. Re-compose after deleting to refresh active output. This cannot be undone.`;
  }

  return `Delete ${countLabel} permanently? This cannot be undone.`;
}

export function DeletePolicyVersionsDialog({
  open,
  onOpenChange,
  request,
  activeComposedPolicyVersionIds,
  isDeleting = false,
  onConfirm,
}: DeletePolicyVersionsDialogProps): React.ReactElement {
  const count = request ? Array.from(new Set(request.policyVersionIds.filter(Boolean))).length : 0;
  const dialogTitle = count === 1 ? "Delete policy version" : "Delete policy versions";
  const deleteButtonLabel =
    count === 1 ? "Delete policy version" : "Delete policy versions";
  const message = request
    ? deletePolicyVersionsMessage(request.policyVersionIds, activeComposedPolicyVersionIds)
    : "";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isDeleting) return;
    onOpenChange(nextOpen);
  };

  const handleCancel = () => {
    if (isDeleting) return;
    onOpenChange(false);
  };

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={dialogTitle}
      showHeader={false}
      showDismissButton={false}
      contentClassName="sm:max-w-md"
      bodyClassName="flex-none overflow-visible pt-0"
      contentProps={{ "data-policy-versions-delete-dialog": "" }}
      footer={
        <div
          data-dashboard-layout-dialog-footer=""
          className="inline-flex max-w-full flex-wrap items-center justify-end gap-2"
        >
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="shrink-0"
            disabled={isDeleting}
            onClick={handleCancel}
          >
            <span className="normal-case tracking-normal">Cancel</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-trade-log-action="delete"
            className="shrink-0 border-destructive/40 text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15 [&_svg]:text-current"
            disabled={isDeleting || request == null || count === 0}
            aria-busy={isDeleting || undefined}
            onClick={onConfirm}
          >
            <span data-font="primary" className="normal-case tracking-normal">
              {isDeleting ? "Deleting…" : deleteButtonLabel}
            </span>
          </Button>
        </div>
      }
    >
      <p className="font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white">
        {message}
      </p>
    </StrategyPineDetailDialog>
  );
}
